/** @module Notices a newly deployed version instead of leaving a stale app on screen. */
import { useCallback, useEffect, useRef, useState } from "react";
import { registerSW } from "virtual:pwa-register";

/** How often the browser is asked whether a new version was deployed. */
const CHECK_MS = 60_000;

export function useAppUpdate(): { ready: boolean; apply: () => void } {
  const [ready, setReady] = useState(false);
  const update = useRef<((reload?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    update.current = registerSW({
      immediate: true,
      onNeedRefresh() {
        setReady(true);
      },
      onRegisteredSW(_url: string, registration: ServiceWorkerRegistration | undefined) {
        if (!registration) return;
        // iOS checks rarely on its own; without this a phone can sit on an old build.
        setInterval(() => {
          void registration.update();
        }, CHECK_MS);
      },
    });
  }, []);

  const apply = useCallback(() => {
    // The service worker does not always take over on its own; reload either way.
    void Promise.resolve(update.current?.(true)).finally(() => {
      window.location.reload();
    });
  }, []);

  return { ready, apply };
}
